import React, { useState } from 'react';
import {
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TableBodyContent from 'common/TableBodyContent/TableBodyContent';
import { useGetSchedulesQuery } from 'store/schedules/schedules';
import { maxItemsPerPage } from 'helpers/pagination';
import ScheduleWorkingDays from 'components/ScheduleWorkingDays/ScheduleWorkingDays';
import tableHeaders from './tableHeaders';

export default function ScheduleForm() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const {
    data: schedulesData = { 'hydra:member': [] },
    isFetching: schedulesFetching,
    isError: schedulesError,
  } = useGetSchedulesQuery({
    itemsPerPage: maxItemsPerPage,
  });

  const schedulesToDisplay = [...schedulesData['hydra:member']].filter(
    (schedule) => schedule.name.toLowerCase().includes(search.toLowerCase()),
  );

  const { getValues, setValue, trigger } = useFormContext();

  const isSelected = (scheduleIri: string) =>
    getValues('schedule') === scheduleIri;

  return (
    <Stack width="70%" pt={3} pl={3} spacing={3}>
      <TextField
        label={t('label.search')}
        fullWidth
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <TableContainer
        sx={{
          height: 'calc(100vh - 38rem)',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.label} align={header.align}>
                  <Typography variant="tableHeader" color="text.secondary">
                    {t(header.label)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell colSpan={tableHeaders.length} />
            </TableRow>
          </TableHead>
          <TableBodyContent
            isFetching={schedulesFetching}
            isError={schedulesError}
            tableHeaders={tableHeaders}
          >
            {schedulesToDisplay.map((schedule) => (
              <TableRow
                key={schedule.id}
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setValue('schedule', schedule['@id']);
                  trigger('schedule');
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {schedule.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ScheduleWorkingDays
                    schedule={schedule}
                    primary={
                      isSelected(schedule['@id']) || !getValues('schedule')
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={
                      isSelected(schedule['@id']) || !getValues('schedule')
                        ? 'primary.main'
                        : 'text.secondary'
                    }
                  >
                    {isSelected(schedule['@id'])
                      ? t('label.selected')
                      : t('label.select')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBodyContent>
        </Table>
      </TableContainer>
    </Stack>
  );
}
